import _ from 'lodash';

import { ArticlePage } from '../model/components/ArticlePage';
import { Media } from '../model/components/Media';
import { Text } from '../model/components/Text';
import {
    isEventsCalendarPost,
    isMultiChoicePost,
    isShopLinkPost,
    isThisOrThatPost,
    isTilePuzzlePost,
    isVideoPost,
    isVoteSliderPost,
    PostData,
} from '../model/PostTypes';
import {
    InteractiveArticlePostData,
    isInteractiveArticlePost,
} from '../model/templates/InteractiveArticleTemplate';
import { isLinkPost, LinkPostData } from '../model/templates/LinkTemplate';
import {
    isStaticArticlePost,
    StaticArticlePostData,
} from '../model/templates/StaticArticleTemplate';
import { isStorefrontLinkPost } from '../model/templates/StorefrontLinkTemplate';
import { EventsCalendarPostData } from '../model/types/EventsCalendarPostData';
import { MultiChoicePostData } from '../model/types/MultiChoicePostData';
import { ShopLinkPostData } from '../model/types/ShopLinkPostData';
import { ThisOrThatPostData } from '../model/types/ThisOrThatPostData';
import { TilePuzzlePostData } from '../model/types/TilePuzzlePostData';
import { VideoPostData } from '../model/types/VideoPostData';
import { VoteSliderPostData } from '../model/types/VoteSliderPostData';
import {
    Action,
    LanguageType,
    TemplateId,
    TranslationTable,
} from '@lifeserver/shared';

import { assert, assertUnreachable } from '../../utils/assert';
import { toError } from '../../utils/error';
import { validateImageDimensions } from '../../utils/validators';

import { Dimensions } from '../../settings';

// ===== Validation Severity =====

export const APP_VER_REGEX = /^\d+\.\d+(\.\d+)?$/;

export type ValidationSeverity = 'warning' | 'error';

export type ValidationMessage = {
    message: string;
    severity: ValidationSeverity;
};

export function warning(message: string): ValidationMessage {
    return { message, severity: 'warning' };
}

export function error(message: string): ValidationMessage {
    return { message, severity: 'error' };
}

export function isValidationMessage(
    value: unknown,
): value is ValidationMessage {
    return (
        typeof value === 'object' &&
        value !== null &&
        'severity' in value &&
        'message' in value
    );
}

export function hasErrors(errors: unknown): boolean {
    if (!errors || typeof errors !== 'object') return false;
    if (isValidationMessage(errors)) {
        return errors.severity === 'error';
    }
    return Object.values(errors as Record<string, unknown>).some(hasErrors);
}

export function validateAction(
    action: Action,
): Record<string, ValidationMessage> | undefined {
    if (action?.$type === 'OpenLocation') {
        if (!action.locationName?.trim()) {
            return {
                locationName: error(
                    'Location is required for OpenLocation action',
                ),
            };
        }
    }
    if (action?.$type === 'OpenDistrict') {
        if (!action.districtName?.trim()) {
            return {
                districtName: error(
                    'District is required for OpenDistrict action',
                ),
            };
        }
    }
    return undefined;
}

interface TranslationTableValidationOptions {
    maxLength?: number;
    required?: boolean;
}

export function validateTranslationTable(
    translationTable: TranslationTable,
    validationOptions: TranslationTableValidationOptions = {},
) {
    const errors: Partial<Record<keyof TranslationTable, ValidationMessage>> =
        {};
    const { maxLength, required } = validationOptions;
    const translations = _.omit(translationTable, ['$type', '$key']);

    if (required) {
        const hasAtLeastOneValue = Object.values(translations).some(
            (text) => text.trim().length > 0,
        );
        if (!hasAtLeastOneValue) {
            errors.$type = error(
                'At least one language translation is required',
            );
            return errors;
        }
    }

    if (typeof maxLength === 'number') {
        for (const [language, text] of Object.entries(translations)) {
            if (text.length > maxLength) {
                errors[language as keyof typeof translations] = warning(
                    `The text length exceeds the maximum of ${maxLength} characters`,
                );
            }
        }
    }
    return _.isEmpty(errors) ? undefined : errors;
}

interface TextValidationOptions {
    text?: TranslationTableValidationOptions;
}

export function validateText(
    text: Text,
    validationOptions: TextValidationOptions = {},
) {
    const textErrors = validateTranslationTable(
        text.text,
        validationOptions.text,
    );
    if (textErrors) {
        return { text: textErrors };
    }
    return;
}

export function validateTotalMaxLength(
    body?: TranslationTable,
    header?: TranslationTable,
    totalMaxLength?: number,
) {
    if (totalMaxLength === undefined) {
        return undefined;
    }

    const errors: Partial<Record<keyof TranslationTable, ValidationMessage>> =
        {};
    for (const lang of Object.values(LanguageType)) {
        const bodyLength = body?.[lang]?.length ?? 0;
        const headerLength = header?.[lang]?.length ?? 0;

        if (bodyLength + headerLength > totalMaxLength) {
            errors[lang] = warning(
                `Body and header length is combined more than the maximum of ${totalMaxLength} characters`,
            );
        }
    }
    return _.isEmpty(errors) ? undefined : errors;
}

interface MediaValidationOptions {
    dimensions?: Dimensions;
}

async function validateMedia(
    media: Media,
    mediaValidationOptions: MediaValidationOptions = {},
) {
    try {
        if (mediaValidationOptions.dimensions && media.mediaType === 'Image') {
            await validateImageDimensions(
                media.url.replace('{0}', '@3x'),
                mediaValidationOptions.dimensions,
            );
        }
    } catch (err) {
        return {
            url: warning(toError(err).message),
        };
    }
    return;
}

interface ArticlePageValidationOptions {
    totalMaxLength?: number;
    header?: {
        text?: TranslationTableValidationOptions;
    };
    body?: {
        text?: TranslationTableValidationOptions;
    };
    background?: MediaValidationOptions;
}

export async function validateArticlePage(
    page: ArticlePage,
    validationOptions: ArticlePageValidationOptions = {},
) {
    const validationErrors: {
        header?: ReturnType<typeof validateText>;
        body?: ReturnType<typeof validateText>;
        background?: Awaited<ReturnType<typeof validateMedia>>;
        totalMaxLength?: ReturnType<typeof validateTotalMaxLength>;
        button?: { action: Record<string, ValidationMessage> };
    } = {};
    const { header, body, background } = page;
    if (header) {
        const headerErrors = validateText(header, validationOptions.header);
        if (headerErrors) {
            validationErrors.header = headerErrors;
        }
    }
    if (body) {
        const bodyErrors = validateText(body, validationOptions.body);
        if (bodyErrors) {
            validationErrors.body = bodyErrors;
        }
    }
    const totalMaxLengthErrors = validateTotalMaxLength(
        page.body?.text,
        page.header?.text,
        validationOptions.totalMaxLength,
    );
    if (totalMaxLengthErrors) {
        validationErrors.totalMaxLength = totalMaxLengthErrors;
    }
    if (background) {
        const backgroundErrors = await validateMedia(
            background,
            validationOptions.background,
        );
        if (backgroundErrors) {
            validationErrors.background = backgroundErrors;
        }
    }
    if (page.button?.action) {
        const actionErrors = validateAction(page.button.action);
        if (actionErrors) {
            validationErrors.button = { action: actionErrors };
        }
    }

    return _.isEmpty(validationErrors) ? undefined : validationErrors;
}

export interface ArticleValidationOptions {
    pages?: {
        [index: number]: ArticlePageValidationOptions;
        default?: ArticlePageValidationOptions;
    };
}

export const isArticleValidationOptions = (
    options: unknown,
): options is ArticleValidationOptions => {
    return (
        options !== null &&
        typeof options === 'object' &&
        'pages' in options &&
        typeof options.pages === 'object'
    );
};

export async function validateArticle(
    postData:
        | StaticArticlePostData
        | InteractiveArticlePostData
        | LinkPostData
        | VoteSliderPostData
        | MultiChoicePostData
        | ThisOrThatPostData,
    validationOptions: ArticleValidationOptions = {},
) {
    const validationErrors: {
        pages?: Record<number, Awaited<ReturnType<typeof validateArticlePage>>>;
    } = {};
    const { pages } = postData;
    for (const [index, page] of pages.entries()) {
        const pageValidationOptions =
            validationOptions.pages?.[index] ||
            validationOptions.pages?.default;
        const errors = await validateArticlePage(page, pageValidationOptions);
        if (errors) {
            _.set(validationErrors, ['pages', index], errors);
        }
    }

    return _.isEmpty(validationErrors) ? undefined : validationErrors;
}

interface ShopLinkValidationOptions {
    coverMedia?: MediaValidationOptions;
    header?: TranslationTableValidationOptions;
}

export async function validateShopLink(
    shopLink: ShopLinkPostData,
    validationOptions: ShopLinkValidationOptions = {},
) {
    const validationErrors: {
        coverMedia?: Awaited<ReturnType<typeof validateMedia>>;
        header?: ReturnType<typeof validateTranslationTable>;
    } = {};
    const { coverMedia, header } = shopLink;
    if (coverMedia) {
        const coverMediaErrors = await validateMedia(
            coverMedia,
            validationOptions.coverMedia,
        );
        if (coverMediaErrors) {
            validationErrors.coverMedia = coverMediaErrors;
        }
    }
    if (header) {
        const headerErrors = validateTranslationTable(
            header,
            validationOptions.header,
        );
        if (headerErrors) {
            validationErrors.header = headerErrors;
        }
    }

    return _.isEmpty(validationErrors) ? undefined : validationErrors;
}

// ===== Poll Validators =====

interface PollValidationOptions extends ArticleValidationOptions {
    question?: TranslationTableValidationOptions;
    minAlternatives?: number;
}

export async function validateThisOrThat(
    postData: ThisOrThatPostData,
    validationOptions: PollValidationOptions = {},
) {
    const validationErrors: {
        pages?: Record<number, Awaited<ReturnType<typeof validateArticlePage>>>;
        polls?: Record<number, Record<string, unknown>>;
    } = {};

    // Validate article pages (background, header, body)
    const articleErrors = await validateArticle(postData, validationOptions);
    if (articleErrors) {
        Object.assign(validationErrors, articleErrors);
    }

    // Validate poll data on each page
    const pollErrors: Record<number, Record<string, unknown>> = {};
    const hasAnyPoll = postData.pages.some((page) => Boolean(page.poll));
    if (!hasAnyPoll) {
        pollErrors[0] = {
            poll: error('At least one page must have a poll configured'),
        };
    }
    for (const [index, page] of postData.pages.entries()) {
        const pageErrors: Record<string, unknown> = {};
        if (page.poll) {
            const minAlts = validationOptions.minAlternatives ?? 2;
            if (page.poll.alternatives.length < minAlts) {
                pageErrors.alternatives = error(
                    `At least ${minAlts} alternatives are required`,
                );
            }
            if (!page.poll.size) {
                pageErrors.size = error('Poll size is required');
            }
            // Validate each alternative has an icon
            const altErrors: Record<
                number,
                Record<string, ValidationMessage>
            > = {};
            for (const [altIdx, alt] of page.poll.alternatives.entries()) {
                const errs: Record<string, ValidationMessage> = {};
                if (!alt.iconName?.trim()) {
                    errs.iconName = error('Icon is required');
                }
                if (!_.isEmpty(errs)) {
                    altErrors[altIdx] = errs;
                }
            }
            if (!_.isEmpty(altErrors)) {
                pageErrors.alternativeErrors = altErrors;
            }
        }
        if (!_.isEmpty(pageErrors)) {
            pollErrors[index] = pageErrors;
        }
    }
    if (!_.isEmpty(pollErrors)) {
        validationErrors.polls = pollErrors;
    }

    return _.isEmpty(validationErrors) ? undefined : validationErrors;
}

export async function validateVoteSlider(
    postData: VoteSliderPostData,
    validationOptions: PollValidationOptions = {},
) {
    const validationErrors: {
        pages?: Record<number, Awaited<ReturnType<typeof validateArticlePage>>>;
        polls?: Record<number, Record<string, unknown>>;
    } = {};

    // Validate article pages (background, header, body)
    const articleErrors = await validateArticle(postData, validationOptions);
    if (articleErrors) {
        Object.assign(validationErrors, articleErrors);
    }

    // Validate poll data on each page
    const pollErrors: Record<number, Record<string, unknown>> = {};
    const hasAnyPoll = postData.pages.some((page) => Boolean(page.poll));
    if (!hasAnyPoll) {
        pollErrors[0] = {
            poll: error('At least one page must have a poll configured'),
        };
    }
    for (const [index, page] of postData.pages.entries()) {
        const pageErrors: Record<string, unknown> = {};
        if (page.poll) {
            if (page.poll.alternatives.length < 1) {
                pageErrors.alternatives = error(
                    'At least 1 alternative is required',
                );
            }
            // Validate each alternative has an icon
            const altErrors: Record<
                number,
                Record<string, ValidationMessage>
            > = {};
            for (const [altIdx, alt] of page.poll.alternatives.entries()) {
                const errs: Record<string, ValidationMessage> = {};
                if (!alt.iconName?.trim()) {
                    errs.iconName = error('Icon is required');
                }
                if (!_.isEmpty(errs)) {
                    altErrors[altIdx] = errs;
                }
            }
            if (!_.isEmpty(altErrors)) {
                pageErrors.alternativeErrors = altErrors;
            }
        }
        if (!_.isEmpty(pageErrors)) {
            pollErrors[index] = pageErrors;
        }
    }
    if (!_.isEmpty(pollErrors)) {
        validationErrors.polls = pollErrors;
    }

    return _.isEmpty(validationErrors) ? undefined : validationErrors;
}

export async function validateMultiChoice(
    postData: MultiChoicePostData,
    validationOptions: PollValidationOptions = {},
) {
    const validationErrors: {
        pages?: Record<number, Awaited<ReturnType<typeof validateArticlePage>>>;
        polls?: Record<number, Record<string, unknown>>;
    } = {};

    // Validate article pages (background, header, body)
    const articleErrors = await validateArticle(postData, validationOptions);
    if (articleErrors) {
        Object.assign(validationErrors, articleErrors);
    }

    // Validate poll data on each page
    const pollErrors: Record<number, Record<string, unknown>> = {};
    for (const [index, page] of postData.pages.entries()) {
        const pageErrors: Record<string, unknown> = {};
        if (!page.poll) {
            pageErrors.poll = error('Poll data is required');
        } else {
            // Validate question
            if (page.poll.question) {
                const questionErrors = validateTranslationTable(
                    page.poll.question,
                    validationOptions.question,
                );
                if (questionErrors) {
                    pageErrors.question = questionErrors;
                }
            }
            // Validate alternatives
            const minAlts = validationOptions.minAlternatives ?? 2;
            if (page.poll.alternatives.length < minAlts) {
                pageErrors.alternatives = error(
                    `At least ${minAlts} alternatives are required`,
                );
            }
            // Validate each alternative has an icon or image
            const altErrors: Record<
                number,
                Record<string, ValidationMessage>
            > = {};
            for (const [altIdx, alt] of page.poll.alternatives.entries()) {
                const errs: Record<string, ValidationMessage> = {};
                if (!alt.iconName?.trim() && !alt.url?.trim()) {
                    errs.icon = error(
                        'An icon or image is required for each alternative',
                    );
                }
                if (!_.isEmpty(errs)) {
                    altErrors[altIdx] = errs;
                }
            }
            if (!_.isEmpty(altErrors)) {
                pageErrors.alternativeErrors = altErrors;
            }
        }
        if (!_.isEmpty(pageErrors)) {
            pollErrors[index] = pageErrors;
        }
    }
    if (!_.isEmpty(pollErrors)) {
        validationErrors.polls = pollErrors;
    }

    return _.isEmpty(validationErrors) ? undefined : validationErrors;
}

interface VideoValidationOptions {
    videoUrl?: MediaValidationOptions;
}

export async function validateVideo(
    videoPost: VideoPostData,
    validationOptions: VideoValidationOptions = {},
) {
    const validationErrors: {
        videoUrl?: Awaited<ReturnType<typeof validateMedia>>;
    } = {};
    const { videoUrl } = videoPost;
    if (videoUrl) {
        const videoUrlErrors = await validateMedia(
            { url: videoUrl, mediaType: 'Video' },
            validationOptions.videoUrl,
        );
        if (videoUrlErrors) {
            validationErrors.videoUrl = videoUrlErrors;
        }
    }

    return _.isEmpty(validationErrors) ? undefined : validationErrors;
}

export interface TilePuzzleValidationOptions {
    gameTextureUrl?: MediaValidationOptions;
    winViewData?: MediaValidationOptions;
}
export async function validateTilePuzzle(
    postData: TilePuzzlePostData,
    validationOptions: TilePuzzleValidationOptions = {},
) {
    const validationErrors: {
        gameTextureUrl?: Awaited<ReturnType<typeof validateMedia>>;
        media?: Awaited<ReturnType<typeof validateMedia>>;
        button?: { action: Record<string, ValidationMessage> };
    } = {};

    if (postData.gameTextureUrl) {
        const gameUrlErrors = await validateMedia(
            { url: postData.gameTextureUrl, mediaType: 'Image' },
            validationOptions.gameTextureUrl,
        );
        if (gameUrlErrors) {
            validationErrors.gameTextureUrl = gameUrlErrors;
        }
    }

    if (postData.winViewData?.media) {
        const winBackgroundMediaErrors = await validateMedia(
            postData.winViewData.media,
            validationOptions.winViewData,
        );
        if (winBackgroundMediaErrors) {
            validationErrors.media = winBackgroundMediaErrors;
        }
    }

    if (postData.winViewData?.button) {
        const actionErrors = validateAction(postData.winViewData.button.action);
        if (actionErrors) {
            validationErrors.button = { action: actionErrors };
        }
    }

    return _.isEmpty(validationErrors) ? undefined : validationErrors;
}

interface EventsCalendarVariantValidationOptions {
    popTextV2s?: { text?: TranslationTableValidationOptions };
}

interface EventsCalendarValidationOptions {
    // One entry per variant, positionally matched to `postData.variants`.
    variants?: EventsCalendarVariantValidationOptions[];
}

function validateEventsCalendarVariant(
    variant: EventsCalendarPostData['variants'][number],
    validationOptions: EventsCalendarVariantValidationOptions,
) {
    const variantErrors: Record<string, unknown> = {};

    if (!variant.imageUrl?.trim()) {
        variantErrors.imageUrl = error('Image URL is required');
    } else {
        try {
            // Keep URL validation permissive but prevent obviously broken input.
            const parsed = new URL(variant.imageUrl);
            if (parsed.protocol === 'blob:') {
                variantErrors.imageUrl = error(
                    'Image is still uploading — please wait for the upload to finish before saving',
                );
            }
        } catch {
            variantErrors.imageUrl = error('Image URL must be a valid URL');
        }
    }

    if (variant.button?.action) {
        const actionErrors = validateAction(variant.button.action);
        if (actionErrors) {
            variantErrors.button = { action: actionErrors };
        }
    }

    if (variant.popTextV2s?.length) {
        const popTextErrors: Record<number, unknown> = {};
        for (const [ptIndex, popTextV2] of variant.popTextV2s.entries()) {
            const textErrors = validateTranslationTable(
                popTextV2.text,
                validationOptions.popTextV2s?.text,
            );
            if (textErrors) {
                popTextErrors[ptIndex] = { text: textErrors };
            }
        }
        if (!_.isEmpty(popTextErrors)) {
            variantErrors.popTextV2s = popTextErrors;
        }
    }

    return _.isEmpty(variantErrors) ? undefined : variantErrors;
}

export function validateEventsCalendar(
    postData: EventsCalendarPostData,
    validationOptions: EventsCalendarValidationOptions = {},
) {
    const validationErrors: Record<string, unknown> = {};

    if (!postData.entryType?.trim()) {
        validationErrors.entryType = error('Entry type is required');
    }

    const variantErrors: Record<number, unknown> = {};
    postData.variants.forEach((variant, index) => {
        const errors = validateEventsCalendarVariant(
            variant,
            validationOptions.variants?.[index] ?? {},
        );
        if (errors) {
            variantErrors[index] = errors;
        }
    });
    if (!_.isEmpty(variantErrors)) {
        validationErrors.variants = variantErrors;
    }

    return _.isEmpty(validationErrors) ? undefined : validationErrors;
}

export const POST_VALIDATIONS = {
    'article-interactive-medium': {
        pages: {
            default: {
                totalMaxLength: 80,
                header: { text: { maxLength: 60 } },
                body: { text: { maxLength: 120 } },
                background: {
                    dimensions: { maxW: 1080, ratio: 1 },
                },
            },
        },
    },
    'article-interactive-large': {
        pages: {
            default: {
                totalMaxLength: 90,
                header: { text: { maxLength: 60 } },
                body: { text: { maxLength: 120 } },
                background: {
                    dimensions: { maxW: 1920, ratio: 9 / 16 },
                },
            },
        },
    },
    'article-static-medium': {
        pages: {
            0: {
                totalMaxLength: 80,
                header: { text: { maxLength: 60 } },
                body: { text: { maxLength: 120 } },
                background: {
                    dimensions: { maxW: 1080, ratio: 1 },
                },
            },
        },
    },
    'article-static-large': {
        pages: {
            0: {
                totalMaxLength: 90,
                header: { text: { maxLength: 60 } },
                body: { text: { maxLength: 120 } },
                background: {
                    dimensions: { maxW: 1920, ratio: 9 / 16 },
                },
            },
        },
    },
    'link-shop-medium': {
        coverMedia: {
            dimensions: { maxW: 724, ratio: 536 / 724 },
        },
        header: { maxLength: 30 },
    },
    'link-shop-large': {
        coverMedia: {
            dimensions: { maxW: 1312, ratio: 536 / 1312 },
        },
        header: { maxLength: 30 },
    },
    'link-medium': {
        pages: {
            0: {
                totalMaxLength: 80,
                background: {
                    dimensions: { maxW: 1080, ratio: 1 },
                },
            },
        },
    },
    'link-large': {
        pages: {
            0: {
                totalMaxLength: 90,
                background: {
                    dimensions: { maxW: 1920, ratio: 9 / 16 },
                },
            },
        },
    },
    'video-medium': {
        videoUrl: {
            dimensions: { maxW: 1080, ratio: 1 },
        },
    },
    'video-large': {
        videoUrl: {
            dimensions: { maxW: 1920, ratio: 9 / 16 },
        },
    },
    'poll-thisorthat-large': {
        pages: {
            0: {
                totalMaxLength: 90,
                header: { text: { maxLength: 60 } },
                body: { text: { maxLength: 120 } },
                background: {
                    dimensions: { maxW: 1920, ratio: 9 / 16 },
                },
            },
        },
        minAlternatives: 2,
    },
    'poll-thisorthat-medium': {
        pages: {
            0: {
                totalMaxLength: 80,
                header: { text: { maxLength: 60 } },
                body: { text: { maxLength: 120 } },
                background: {
                    dimensions: { maxW: 1080, ratio: 1 },
                },
            },
        },
        minAlternatives: 2,
    },
    'poll-voteslider-medium': {
        pages: {
            0: {
                totalMaxLength: 80,
                header: { text: { maxLength: 60 } },
                body: { text: { maxLength: 120 } },
                background: {
                    dimensions: { maxW: 1080, ratio: 1 },
                },
            },
        },
        minAlternatives: 1,
    },
    'poll-voteslider-large': {
        pages: {
            0: {
                totalMaxLength: 90,
                header: { text: { maxLength: 60 } },
                body: { text: { maxLength: 120 } },
                background: {
                    dimensions: { maxW: 1920, ratio: 9 / 16 },
                },
            },
        },
        minAlternatives: 1,
    },
    'tile-puzzle-large': {
        gameMediaUrl: {
            dimensions: { maxW: 1080, ratio: 1 },
        },
        winViewData: {
            dimensions: { maxW: 1920, ratio: 9 / 16 },
        },
    },
    'link-storefront-medium': {
        pages: {
            0: {
                totalMaxLength: 80,
                background: {
                    dimensions: { maxW: 1080, ratio: 1 },
                },
            },
        },
    },
    'link-storefront-large': {
        pages: {
            0: {
                totalMaxLength: 90,
                background: {
                    dimensions: { maxW: 1920, ratio: 9 / 16 },
                },
            },
        },
    },
    'poll-multichoice-medium': {
        pages: {
            0: {},
        },
        question: { required: true },
        minAlternatives: 2,
    },
    'poll-multichoice-large': {
        pages: {
            0: {},
        },
        question: { required: true },
        minAlternatives: 2,
    },
    'event-calendar-small': {
        variants: [
            {
                title: { maxLength: 30, required: true },
                popTextV2s: { text: { required: true } },
            },
            {
                title: { maxLength: 30, required: true },
                popTextV2s: { text: { required: true } },
            },
        ],
    },
    'event-calendar-medium': {
        variants: [
            {
                title: { maxLength: 60, required: true },
                popTextV2s: { text: { required: true } },
            },
            {
                title: { maxLength: 60, required: true },
                popTextV2s: { text: { required: true } },
            },
        ],
    },
};
export const validatePostData = async (
    templateId: TemplateId,
    postData: PostData,
): Promise<Record<string, unknown> | undefined> => {
    if (!postData) {
        return undefined;
    }

    switch (templateId) {
        case 'article-interactive-medium':
        case 'article-interactive-large':
            assert(isInteractiveArticlePost(postData));
            return validateArticle(postData, POST_VALIDATIONS[templateId]);
        case 'article-static-medium':
        case 'article-static-large':
            assert(isStaticArticlePost(postData));
            return validateArticle(postData, POST_VALIDATIONS[templateId]);
        case 'link-medium':
        case 'link-large':
            assert(isLinkPost(postData));
            return validateArticle(postData, POST_VALIDATIONS[templateId]);
        case 'link-storefront-large':
        case 'link-storefront-medium':
            assert(isStorefrontLinkPost(postData));
            return validateArticle(postData, POST_VALIDATIONS[templateId]);
        case 'link-shop-medium':
        case 'link-shop-large':
            assert(isShopLinkPost(postData));
            return validateShopLink(postData, POST_VALIDATIONS[templateId]);
        case 'video-medium':
        case 'video-large':
            assert(isVideoPost(postData));
            return validateVideo(postData, POST_VALIDATIONS[templateId]);
        case 'poll-voteslider-medium':
        case 'poll-voteslider-large':
            assert(isVoteSliderPost(postData));
            return validateVoteSlider(postData, POST_VALIDATIONS[templateId]);
        case 'poll-thisorthat-medium':
        case 'poll-thisorthat-large':
            assert(isThisOrThatPost(postData));
            return validateThisOrThat(postData, POST_VALIDATIONS[templateId]);
        case 'poll-multichoice-medium':
        case 'poll-multichoice-large':
            assert(isMultiChoicePost(postData));
            return validateMultiChoice(postData, POST_VALIDATIONS[templateId]);
        case 'tile-puzzle-large':
            assert(isTilePuzzlePost(postData));
            return validateTilePuzzle(postData, POST_VALIDATIONS[templateId]);
        case 'event-calendar-small':
        case 'event-calendar-medium':
            assert(isEventsCalendarPost(postData));
            return validateEventsCalendar(
                postData,
                POST_VALIDATIONS[templateId],
            );
        default:
            throw assertUnreachable(templateId);
    }
};
